import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDeviceType, Sort16, IconButton, Radio, BulletList16 as IconList16 } from '@cian/ui-kit';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { BreadCrumbsContainer } from '../BreadCrumbs';
import { TagScrollingContainer } from '../TagScrolling';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { selectCurrentPageMeta } from '../../selectors/pagesMeta';
import { EOrdering } from '../../repositories/journal/v2/get-article-list';
import { prepareListingOrdering, useCurrentListingStatus } from '../../utils';
import { IconTable16 } from '../../components/Icons/IconTable16';
import { Curtain } from '../../components/Curtain';
import { ModalHeader } from '../../components/ModalHeader';
import { EListingTypes } from '../../types/listing';
import { selectFavoriteListingType } from '../../selectors/settings/selectFavoriteListingType';
import { saveFavoriteListingType } from '../../actions/settings/saveFavoriteListingType';
import { tryToSetFavoriteListingType } from '../../actions/settings/tryToSetFavoriteListingType';
import * as s from './ListingHeaderContainer.css';

/**
 * Шапка любого листинга
 */
export const ListingHeaderContainer = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const isPhone = () => deviceType === 'phone';
  const isDesktop = () => deviceType === 'desktop';
  const { pathname, search } = useLocation();
  const { title, pathname: currentPageMetaPathname } = useSelector((state: IApplicationState) =>
    selectCurrentPageMeta(state, pathname),
  );
  const listingStatus = useCurrentListingStatus();
  const favoriteListingType = useSelector(selectFavoriteListingType);
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [activeOrdering, setActiveOrdering] = useState<EOrdering>(EOrdering.Date_publish);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const history = useHistory();
  const orderingOptions = useMemo(
    () => [
      { name: EOrdering.Date_publish, title: 'Новое' },
      { name: EOrdering.Comments_count, title: 'Обсуждаемое' },
      { name: EOrdering.Num_views, title: 'Популярное' },
    ],
    [],
  );
  const listingTypeOptions = useMemo(
    () => [
      { name: EListingTypes.Cards, icon: <IconTable16 /> },
      { name: EListingTypes.List, icon: <IconList16 /> },
    ],
    [],
  );

  /**
   * Устанавливаем значение favoriteListingType
   */
  useEffect(() => {
    if (favoriteListingType !== null) {
      return;
    }

    dispatch(tryToSetFavoriteListingType());
  }, [dispatch, favoriteListingType]);

  /**
   * Сохраняет состояние сортировки в стэйт
   */
  useEffect(() => {
    const qsOrdering = searchParams.get('ordering');
    const qsOrderingPrepared = qsOrdering ? prepareListingOrdering(qsOrdering) : '';

    if (
      !qsOrderingPrepared ||
      activeOrdering === qsOrderingPrepared ||
      !orderingOptions.find(orderingOption => orderingOption.name === qsOrderingPrepared)
    ) {
      return;
    }

    setActiveOrdering(qsOrderingPrepared);
  }, [activeOrdering, orderingOptions, searchParams]);

  /**
   * Закрывает модалку при смене вьюпорта на десктоп
   */
  useEffect(() => {
    if (!isModalOpened || deviceType !== 'desktop') {
      return;
    }

    setIsModalOpened(false);
  }, [deviceType, isModalOpened]);

  /**
   * Устанавливает сортировку
   */
  const handleOrderingClick = useCallback(
    (orderingName: EOrdering) => {
      searchParams.set('ordering', orderingName);
      searchParams.delete('page');
      history.push(`${currentPageMetaPathname}?${searchParams.toString()}`);
    },
    [currentPageMetaPathname, history, searchParams],
  );

  /**
   * Устанавливает сортировку и закрывает модалку с паузой
   */
  const handleModalOrderingClick = useCallback(
    (e: React.MouseEvent, orderingName: EOrdering) => {
      e.preventDefault();
      handleOrderingClick(orderingName);
      setIsModalOpened(false);
    },
    [handleOrderingClick],
  );

  /**
   * Устанавливает тип списка
   */
  const handleListingTypeClick = useCallback(
    (listingTypeName: EListingTypes) => {
      dispatch(saveFavoriteListingType(listingTypeName));
    },
    [dispatch],
  );

  /** Переключает видимость модалки */
  const handleToggleModal = useCallback(() => {
    setIsModalOpened(isModalOpened => !isModalOpened);
  }, []);

  return (
    <div className={s['wrapper']}>
      <div className={s['title-wrapper']}>
        {title && <h1 className={s['title']}>{title}</h1>}

        <div className={s['ordering-buttons-wrapper']}>
          {/* Кнопка, вызывающая модалку с сортировкой */}
          {!isDesktop() && (
            <div className={`${s['ordering-modal-button-wrapper']} ${isModalOpened ? s['_active'] : ''}`}>
              <IconButton icon={<Sort16 />} onClick={handleToggleModal} disabled={listingStatus === 'loading'} />
            </div>
          )}

          {/* Кнопки сортировки */}
          {isDesktop() &&
            orderingOptions.map(({ name, title }, index) => (
              <div
                key={index}
                className={`${s['ordering-button']} ${activeOrdering === name ? s['_active'] : ''}`}
                onClick={() => handleOrderingClick(name)}
              >
                <IconButton
                  icon={<span>{title}</span>}
                  onClick={() => handleOrderingClick(name)}
                  disabled={listingStatus === 'loading'}
                />
              </div>
            ))}
        </div>

        {/* Кнопки выбора типа листинга */}
        {!isPhone() && (
          <div className={s['listing-type-wrapper']}>
            {listingTypeOptions.map(({ name, icon }, index) => (
              <div
                key={index}
                className={`${s['listing-type-button-wrapper']} ${favoriteListingType === name ? s['_active'] : ''}`}
              >
                <IconButton
                  icon={icon}
                  onClick={() => handleListingTypeClick(name)}
                  disabled={listingStatus === 'loading'}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Слайдер тегов */}
      <div className={s['tag-scrolling-wrapper']}>
        <TagScrollingContainer />
      </div>

      {/* Хлебные крошки */}
      {!isPhone() && (
        <div className={s['bread-crumbs-wrapper']}>
          <BreadCrumbsContainer />
        </div>
      )}

      {/* Шторка с сортировкой */}
      {!isDesktop() && (
        <Curtain isOpen={isModalOpened} onClose={handleToggleModal}>
          <>
            <ModalHeader onClose={handleToggleModal}>Сортировать</ModalHeader>
            <div className={s['modal-content']}>
              {orderingOptions.map(({ name, title }, index) => (
                <div
                  key={index}
                  className={`${s['modal-ordering-button']} ${activeOrdering === name ? s['_active'] : ''}`}
                  onClick={e => handleModalOrderingClick(e, name)}
                >
                  <span>{title}</span>
                  <Radio name={'ordering'} value={name} checked={activeOrdering === name} />
                </div>
              ))}
            </div>
          </>
        </Curtain>
      )}
    </div>
  );
};
